import { useEffect, useMemo, useState } from 'react';
import PeriodSelector from '../components/PeriodSelector';
import { usePeriod } from '../contexts/PeriodContext';
import { useUseCase } from '../contexts/UseCaseContext';
import { API_ENDPOINTS } from '../config/api';
import { logger } from '../utils/logger';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { Badge, type BadgeTone } from '../components/ui/badge';

interface CSVRow {
  [key: string]: string;
}

const parseCSV = (text: string): CSVRow[] => {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(h => h.trim());
  const rows: CSVRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    const row: CSVRow = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    rows.push(row);
  }

  return rows;
};

interface BenchmarkPercentiles {
  p25?: number;
  p50?: number;
  p75?: number;
  p90?: number;
}

interface BenchmarkConfig {
  co2e_per_mile?: {
    industry_average?: number;
    percentiles?: BenchmarkPercentiles;
    source?: string;
    last_updated?: string;
    is_estimate?: boolean;
  };
}

const getDefaultBenchmarks = (serviceType: string): BenchmarkConfig => {
  if (serviceType === 'logistics') {
    return {
      co2e_per_mile: {
        industry_average: 1.2,
        percentiles: { p25: 1.0, p50: 1.2, p75: 1.4, p90: 1.6 },
        source: 'UK Government GHG Conversion Factors 2025 (baseline estimate - not actual industry data)',
        last_updated: '2025',
        is_estimate: true,
      },
    };
  }

  return {
    co2e_per_mile: {
      industry_average: 0.8,
      percentiles: { p25: 0.7, p50: 0.8, p75: 0.9, p90: 1.0 },
      source: 'UK Government GHG Conversion Factors 2025 (baseline estimate - not actual industry data)',
      last_updated: '2025',
      is_estimate: true,
    },
  };
};

const getTotalEmissionsFromRow = (csvRow: CSVRow): number => {
  const active = parseFloat(csvRow.active_emission_kg || '0');
  const idle = parseFloat(csvRow.idle_emission_kg || '0');
  return active + idle;
};

const getFleetBenchmarkComparison = (vehicleTypeLevelCSV: CSVRow[], benchmarkConfig: BenchmarkConfig | null) => {
  if (!benchmarkConfig || vehicleTypeLevelCSV.length === 0) return null;

  const totalEmissions = vehicleTypeLevelCSV.reduce((sum, row) => sum + getTotalEmissionsFromRow(row), 0);
  const totalMiles = vehicleTypeLevelCSV.reduce((sum, row) => {
    const miles = parseFloat(row.total_miles || row.Total_Miles || row.miles || row.Miles || '0');
    return sum + miles;
  }, 0);

  const fleetCo2ePerMile = totalMiles > 0 ? totalEmissions / totalMiles : 0;
  const industryAverage = benchmarkConfig.co2e_per_mile?.industry_average || 0;

  const percentiles = benchmarkConfig.co2e_per_mile?.percentiles || {};
  let percentile = 50;
  // Optional percentile fields: undefined values compare false, exactly as the
  // previous untyped access did, so the fall-through to 95 is preserved.
  if (fleetCo2ePerMile <= (percentiles.p25 as number)) percentile = 25;
  else if (fleetCo2ePerMile <= (percentiles.p50 as number)) percentile = 50;
  else if (fleetCo2ePerMile <= (percentiles.p75 as number)) percentile = 75;
  else if (fleetCo2ePerMile <= (percentiles.p90 as number)) percentile = 90;
  else percentile = 95;

  const percentDifference = industryAverage > 0
    ? ((industryAverage - fleetCo2ePerMile) / industryAverage) * 100
    : 0;

  let performanceStatus: 'excellent' | 'good' | 'average' | 'below_average' = 'average';
  if (percentDifference >= 20) performanceStatus = 'excellent';
  else if (percentDifference >= 10) performanceStatus = 'good';
  else if (percentDifference >= -10) performanceStatus = 'average';
  else performanceStatus = 'below_average';

  return {
    fleetCo2ePerMile,
    industryAverage,
    percentDifference,
    percentile,
    performanceStatus,
    source: benchmarkConfig.co2e_per_mile?.source || 'Industry estimates',
    lastUpdated: benchmarkConfig.co2e_per_mile?.last_updated || '2025',
  };
};

export default function ClientReporting() {
  const { selectedPeriod } = usePeriod();
  const { useCase } = useUseCase();
  const serviceType = useCase === 'emergency' ? 'emergency_services' : 'logistics';
  const [vehicleTypeLevelCSV, setVehicleTypeLevelCSV] = useState<CSVRow[]>([]);
  const [benchmarkConfig, setBenchmarkConfig] = useState<BenchmarkConfig | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    const period = selectedPeriod as string | null;

    if (!period) {
      setVehicleTypeLevelCSV([]);
      setBenchmarkConfig(getDefaultBenchmarks(serviceType));
      return undefined;
    }

    setLoading(true);

    const loadData = async () => {
      try {
        const vehiclePromise = authenticatedFetch(API_ENDPOINTS.vehicleTypeLevelCSV(period, serviceType), {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        }).then(res => (res.ok ? res.text() : ''));

        const benchmarkPromise = authenticatedFetch(API_ENDPOINTS.benchmarkConfig(serviceType)).then(res => {
          if (res.status === 404) return null;
          if (!res.ok) throw new Error(`Benchmark config failed: ${res.status}`);
          return res.json();
        });

        const [vehicleCsvText, benchmarkCfg] = await Promise.all([vehiclePromise, benchmarkPromise]);

        if (isCancelled) return;

        setVehicleTypeLevelCSV(vehicleCsvText ? parseCSV(vehicleCsvText) : []);
        if (benchmarkCfg) {
          setBenchmarkConfig(benchmarkCfg);
        } else {
          setBenchmarkConfig(getDefaultBenchmarks(serviceType));
        }
      } catch (error) {
        if (isCancelled) return;
        logger.error('Failed to load client reporting data:', error);
        setVehicleTypeLevelCSV([]);
        setBenchmarkConfig(getDefaultBenchmarks(serviceType));
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  }, [selectedPeriod, serviceType]);

  const benchmarkComparison = useMemo(() => (
    getFleetBenchmarkComparison(vehicleTypeLevelCSV, benchmarkConfig)
  ), [vehicleTypeLevelCSV, benchmarkConfig]);

  const isBaselineEstimate = benchmarkConfig?.co2e_per_mile?.source?.includes('baseline') ||
    benchmarkConfig?.co2e_per_mile?.source?.includes('Baseline') ||
    benchmarkComparison?.source?.includes('baseline') ||
    benchmarkComparison?.source?.includes('Baseline');

  const getPerformanceColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-success';
      case 'average': return 'text-warning';
      case 'below_average': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getPerformanceTone = (status: string): BadgeTone => {
    switch (status) {
      case 'excellent': return 'info';
      case 'good': return 'success';
      case 'average': return 'warning';
      case 'below_average': return 'destructive';
      default: return 'neutral';
    }
  };

  const getPerformanceLabel = (status: string) => {
    switch (status) {
      case 'excellent': return 'Excellent';
      case 'good': return 'Good';
      case 'average': return 'Average';
      case 'below_average': return 'Below Average';
      default: return 'Unknown';
    }
  };

  return (
    <div className="space-y-6 overflow-visible">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Client Scope III Reporting</h2>
          <p className="text-muted-foreground text-sm">
            Share client-ready carbon performance with industry benchmark context for the selected reporting period.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <PeriodSelector />
        </div>
      </div>

      {loading ? (
        <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
          <p className="text-muted-foreground">Loading benchmark data...</p>
        </div>
      ) : benchmarkComparison ? (
        <div className="bg-card rounded-lg shadow-sm p-4 border border-border mb-6 hover:border-ring hover:shadow-lg hover:shadow-ring/50 transition-all">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">Performance vs Industry Benchmarks</h3>
            <div className="flex items-center gap-2">
              {isBaselineEstimate && (
                <Badge
                  tone="warning"
                  className="px-2 py-1 cursor-help"
                  title="These benchmarks are baseline estimates from UK Government GHG Conversion Factors, not actual industry survey data. Update benchmark_config.json with real industry data for accurate comparisons."
                >
                  ⚠️ Baseline Estimate
                </Badge>
              )}
              <Badge tone={getPerformanceTone(benchmarkComparison.performanceStatus)} className="px-3 py-1">
                {getPerformanceLabel(benchmarkComparison.performanceStatus)}
              </Badge>
            </div>
          </div>
          {isBaselineEstimate && (
            <div className="mb-3 p-2 bg-warning/30 border border-warning/50 rounded text-xs text-warning">
              <strong>Note:</strong> The industry average ({benchmarkComparison.industryAverage.toFixed(2)} kg CO₂e/mile) is a <strong>baseline estimate</strong> derived from UK Government GHG Conversion Factors 2025, not actual industry survey data. For accurate comparisons, update <code className="bg-background px-1 rounded">benchmark_config.json</code> with real industry benchmarks from associations (FTA, RHA), research studies, or benchmarking services.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-background/50 rounded-lg p-3 border border-border">
              <div className="text-xs text-muted-foreground mb-1">Your Fleet</div>
              <div className="text-2xl font-bold text-white">
                {benchmarkComparison.fleetCo2ePerMile.toFixed(2)} kg CO₂e/mile
              </div>
            </div>
            <div className="bg-background/50 rounded-lg p-3 border border-border">
              <div className="text-xs text-muted-foreground mb-1">Industry Average</div>
              <div className="text-2xl font-bold text-muted-foreground">
                {benchmarkComparison.industryAverage.toFixed(2)} kg CO₂e/mile
              </div>
              {isBaselineEstimate && (
                <div className="text-xs text-warning mt-1">(Baseline estimate)</div>
              )}
            </div>
            <div className="bg-background/50 rounded-lg p-3 border border-border">
              <div className="text-xs text-muted-foreground mb-1">Performance</div>
              <div className={`text-2xl font-bold ${getPerformanceColor(benchmarkComparison.performanceStatus)}`}>
                {benchmarkComparison.percentDifference > 0 ? '+' : ''}{benchmarkComparison.percentDifference.toFixed(1)}%
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {benchmarkComparison.percentDifference > 0 ? 'Better' : benchmarkComparison.percentDifference < 0 ? 'Worse' : 'Equal'} than industry average
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center justify-between text-xs flex-wrap gap-2">
              <div className="flex items-center gap-2 text-muted-foreground flex-wrap">
                <span>Percentile Ranking:</span>
                <span className="text-white font-medium">{benchmarkComparison.percentile}th percentile</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground">Source: {benchmarkComparison.source}</span>
              </div>
              <span className="text-muted-foreground">Last updated: {benchmarkComparison.lastUpdated}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
          <p className="text-muted-foreground">No benchmark data available for this period.</p>
        </div>
      )}
    </div>
  );
}
